import { ActNatures, OsteotomyTypes } from "../enums/SurgicalActsEnum";
import { SelectionNature } from "../enums/VertebraSelectionEnum";

export const surgeryUtils = {
  mapLandmarkNatureToOsteotomyType: landmarkNature => {
    switch (landmarkNature) {
      case SelectionNature.VERTEBRAE:
        return OsteotomyTypes.GRADE_3_PSO;
      case SelectionNature.PEDICLES:
        return OsteotomyTypes.GRADE_2_PCO;
      default:
        return OsteotomyTypes.GRADE_3_PSO;
    }
  },
};

export const generateSurgicalActKey = surgicalAct => {
  if (!surgicalAct || !surgicalAct.actNature) {
    throw new Error("Invalid surgical act: missing actNature");
  }

  const { actNature, vertebra, vertebraBelow, side } = surgicalAct;

  // Singleton act types (no vertebra requirement)
  if (actNature === ActNatures.ReciprocalChange) {
    return `${actNature}`;
  }
  if (actNature === ActNatures.GlobalAlignment) {
    return `${actNature}`;
  }

  if (!vertebra) {
    throw new Error("Invalid surgical act: missing vertebra");
  }

  switch (actNature) {
    case ActNatures.Cage:
      // Cages are unique per vertebra pair (upper and lower)
      // Multiple cages can exist between different vertebra pairs
      return `${actNature}_${vertebra}_${vertebraBelow || "single"}`;

    case ActNatures.Osteotomy:
      // Osteotomies are unique per vertebra
      // Only one osteotomy can exist per vertebra
      return `${actNature}_${vertebra}`;

    case ActNatures.SegmentalTransformation:
      // Segmental transformations are unique per vertebra
      // Only one transformation can exist per vertebra
      return `${actNature}_${vertebra}`;

    case ActNatures.Rod:
      // Rods are uniquely identified by rodPairId and side
      // This ensures that when a rod is modified, the old one is replaced
      const rodPairId = surgicalAct.rodPairId || vertebra;
      return `${actNature}_${rodPairId}_${side}`;

    default:
      // Default case for any new surgical act types
      return `${actNature}_${vertebra}`;
  }
};

/**
 * Validates if a surgical act has all required properties for key generation
 *
 * @param {Object} surgicalAct - The surgical act to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validateSurgicalActForKey = surgicalAct => {
  if (!surgicalAct) return false;
  if (!surgicalAct.actNature) return false;
  if (
    surgicalAct.actNature !== ActNatures.ReciprocalChange &&
    surgicalAct.actNature !== ActNatures.GlobalAlignment &&
    !surgicalAct.vertebra
  )
    return false;

  // Special validation for cage acts
  if (surgicalAct.actNature === ActNatures.Cage) {
    // vertebraBelow is optional but recommended
    if (!surgicalAct.vertebraBelow) {
    }
  }

  return true;
};

/**
 * Gets a human-readable description of a surgical act for logging/debugging
 *
 * @param {Object} surgicalAct - The surgical act
 * @returns {string} Human-readable description
 */
export const getSurgicalActDescription = surgicalAct => {
  if (!validateSurgicalActForKey(surgicalAct)) {
    return "Invalid surgical act";
  }

  const { actNature, vertebra, vertebraBelow } = surgicalAct;

  switch (actNature) {
    case ActNatures.Cage:
      return `Cage between ${vertebra} and ${vertebraBelow || "single level"}`;
    case ActNatures.Osteotomy:
      return `Osteotomy at ${vertebra}`;
    case ActNatures.SegmentalTransformation:
      return `Segmental transformation at ${vertebra}`;
    case ActNatures.Rod:
      return `Rod at ${vertebra} (${surgicalAct.side || "unknown side"})`;
    default:
      return `${actNature} at ${vertebra}`;
  }
};
